import { useMemo, useRef, useState, type RefObject } from "react";
import { Dialog, Popover } from "react-aria-components";
import { Check, ChevronLeft, Folder, Plus, Search, X } from "lucide-react";
import { useCoShellPortal } from "@/app/routes/dashboard/CoShellPortalContext";
import type { Brand } from "@/hooks/useGetBrands";
import type { Reference, ReferenceCollection } from "@/types/reference.type";
import { cn } from "@/utils/helpers";
import {
  isTempCollection,
  useCreateCollection,
  useToggleCollection,
} from "../api/useReferenceCollections";
import { ReferenceBrandSelect, ReferenceFieldRow } from "./ReferenceBrandPicker";
import { CollectionCover } from "./CollectionsRow";

/*
 * References / Collection picker (library, style B): Pinterest's picker. 380
 * wide, solid surface, radius 12. Search, big covers, a square check per
 * collection (checked means it is in), New collection at the foot. Ticking acts
 * at once, so there is no Save; closing keeps what was ticked. New collection
 * turns the picker into a small form (Collection picker · New collection): the
 * first reference, a name, the brand it is for, and Create.
 */

const NAME_MAX = 80;

/** Which collections can take these references: a brand's collection holds that
 *  brand's references and Everyone's; an Everyone collection holds any. */
export function eligibleCollections(collections: ReferenceCollection[], references: Reference[]) {
  const brands = new Set(references.map((r) => r.brandId).filter((b): b is string => b !== null));
  return collections.filter(
    (c) => c.brandId === null || [...brands].every((b) => b === c.brandId),
  );
}

export default function CollectionPicker({
  references,
  anchor,
  collections,
  brands,
  activeBrandId,
  allowEveryone,
  canManage,
  onClose,
  startNew = false,
  wallCollectionId = null,
}: {
  references: Reference[];
  anchor: HTMLElement;
  collections: ReferenceCollection[];
  brands: Brand[];
  activeBrandId: string | null;
  allowEveryone: boolean;
  /** May this viewer tick this collection (a client only fills their own). */
  canManage: (collection: ReferenceCollection) => boolean;
  onClose: () => void;
  /** Open straight on the New collection form. */
  startNew?: boolean;
  /** The collection whose wall is open. Unticking it removes the tile this
   *  picker is anchored to, so the picker closes with the tile. */
  wallCollectionId?: string | null;
}) {
  const portal = useCoShellPortal();
  const triggerRef = useRef<HTMLElement>(anchor) as RefObject<HTMLElement>;
  const [view, setView] = useState<"list" | "new">(startNew ? "new" : "list");

  return (
    <Popover
      triggerRef={triggerRef}
      isOpen
      onOpenChange={(open) => !open && onClose()}
      placement="top"
      offset={10}
      UNSTABLE_portalContainer={portal ?? undefined}
      className="ref-picker z-[var(--z-index-popover)] outline-none"
    >
      <Dialog
        aria-label={view === "new" ? "New collection" : "Add to a collection"}
        className="flex max-h-[inherit] flex-col outline-none"
      >
        {view === "list" ? (
          <PickerList
            references={references}
            collections={collections}
            brands={brands}
            activeBrandId={activeBrandId}
            canManage={canManage}
            wallCollectionId={wallCollectionId}
            onNew={() => setView("new")}
            onClose={onClose}
          />
        ) : (
          <NewCollectionForm
            references={references}
            brands={brands}
            defaultBrandId={references[0]?.brandId ?? (allowEveryone ? null : activeBrandId)}
            allowEveryone={allowEveryone}
            onBack={startNew ? onClose : () => setView("list")}
            onDone={onClose}
          />
        )}
      </Dialog>
    </Popover>
  );
}

function PickerList({
  references,
  collections,
  brands,
  activeBrandId,
  canManage,
  wallCollectionId,
  onNew,
  onClose,
}: {
  references: Reference[];
  collections: ReferenceCollection[];
  brands: Brand[];
  activeBrandId: string | null;
  canManage: (collection: ReferenceCollection) => boolean;
  wallCollectionId: string | null;
  onNew: () => void;
  onClose: () => void;
}) {
  const toggle = useToggleCollection();
  const [q, setQ] = useState("");

  /* Grouped by whose they are: this brand's first, then other brands', then
     Everyone's, each under its mono label. */
  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const fits = eligibleCollections(collections, references).filter(
      (c) => canManage(c) && (!term || c.name.toLowerCase().includes(term)),
    );
    const nameOf = (id: string | null) =>
      id === null ? "Everyone" : (brands.find((b) => b.id === id)?.name ?? "Another brand");
    const keys = [...new Set(fits.map((c) => c.brandId))].sort((a, b) =>
      a === activeBrandId ? -1 : b === activeBrandId ? 1 : a === null ? 1 : b === null ? -1 : 0,
    );
    return keys.map((key) => ({
      key: key ?? "everyone",
      label: `${nameOf(key)}'s collections`,
      items: fits.filter((c) => c.brandId === key),
    }));
  }, [collections, references, brands, activeBrandId, canManage, q]);

  const title =
    references.length === 1 ? "Add to a collection" : `Add ${references.length} to a collection`;

  return (
    <>
      <div className="flex items-center justify-between px-[18px] pb-3 pt-[18px]">
        <h2 className="ref-picker-title">{title}</h2>
        <button type="button" className="ref-icon-close" aria-label="Close" onClick={onClose}>
          <X aria-hidden className="size-4" />
        </button>
      </div>
      <div className="px-[18px]">
        <label className="ref-picker-search co-glass-control">
          <Search aria-hidden className="size-3.5 text-co-faint" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search collections"
            aria-label="Search collections"
          />
        </label>
      </div>
      <div className="custom-scrollbar min-h-0 flex-1 overflow-y-auto">
        {groups.map((group) => (
          <section key={group.key} aria-label={group.label}>
            <p className="ref-mono-label px-[18px] pb-1.5 pt-4">{group.label}</p>
            <ul className="flex flex-col gap-2 px-2 pb-2.5 pt-0.5">
              {group.items.map((collection) => {
                const inIt = references.every((r) => (r.collectionIds ?? []).includes(collection.id));
                const pending = isTempCollection(collection.id);
                return (
                  <li key={collection.id}>
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={inIt}
                      disabled={pending}
                      className="ref-picker-row"
                      onClick={() => {
                        const add = !inIt;
                        toggle.mutate({ collectionId: collection.id, references, add });
                        if (!add && wallCollectionId === collection.id) onClose();
                      }}
                    >
                      <CollectionCover collection={collection} size={56} />
                      <span className="flex min-w-0 flex-1 flex-col gap-[3px] text-start">
                        <span className="truncate text-[15px] font-semibold text-co-text">{collection.name}</span>
                        <span className="text-[13px] text-co-muted">
                          {collection.count === 0
                            ? "Empty"
                            : `${collection.count} ${collection.count === 1 ? "reference" : "references"}`}
                        </span>
                      </span>
                      <span className={cn("ref-square-check", inIt && "is-on")} aria-hidden>
                        {inIt ? <Check className="size-3" strokeWidth={3} /> : null}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
        {groups.length === 0 ? (
          <p className="px-[18px] py-4 text-[13px] text-co-muted">
            {q.trim() ? "No collection matches." : "No collections yet. Start one below."}
          </p>
        ) : null}
      </div>
      <div className="px-2 pb-2">
        <button type="button" className="ref-picker-row ref-picker-new" onClick={onNew}>
          <span className="ref-picker-plus" aria-hidden>
            <Plus className="size-5" />
          </span>
          <span className="text-[15px] font-semibold text-co-accent">New collection</span>
        </button>
      </div>
    </>
  );
}

export function NewCollectionForm({
  references,
  brands,
  defaultBrandId,
  allowEveryone,
  onBack,
  onDone,
}: {
  references: Reference[];
  brands: Brand[];
  defaultBrandId: string | null;
  allowEveryone: boolean;
  onBack: () => void;
  onDone: (collection?: { name: string }) => void;
}) {
  const create = useCreateCollection();
  const [name, setName] = useState("");
  const [brandId, setBrandId] = useState<string | null>(defaultBrandId);
  const first = references[0];
  const firstPicture = first ? (first.imageUrl ?? first.thumbnailUrl) : null;
  /* A reference tagged for a brand can only go into that brand's collection or
     Everyone's, so the FOR choices narrow to those. */
  const fixedBrand = references.find((r) => r.brandId)?.brandId ?? null;
  const brandChoices = fixedBrand ? brands.filter((b) => b.id === fixedBrand) : brands;
  const trimmed = name.trim();

  const submit = () => {
    if (!trimmed) return;
    create.mutate({ name: trimmed, brandId, references });
    onDone({ name: trimmed });
  };

  return (
    <form
      className="flex flex-col"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="flex items-center gap-2.5 pb-3 pl-3.5 pr-[18px] pt-[18px]">
        <button type="button" className="ref-icon-close" aria-label="Back" onClick={onBack}>
          <ChevronLeft aria-hidden className="size-[18px] text-co-text2" />
        </button>
        <h2 className="ref-picker-title">New collection</h2>
      </div>
      <div className="flex flex-col gap-3.5 px-[18px] pb-[18px] pt-1">
        {first ? (
          <div className="flex items-center gap-3.5">
            {firstPicture ? (
              <img src={firstPicture} alt="" referrerPolicy="no-referrer" className="size-[88px] shrink-0 rounded-card object-cover" />
            ) : (
              <span className="grid size-[88px] shrink-0 place-items-center rounded-card border border-co-line2" aria-hidden>
                <Folder className="size-5 text-co-faint" />
              </span>
            )}
            <span className="flex min-w-0 flex-col gap-1">
              <span className="ref-mono-label">
                {references.length === 1 ? "First reference" : `${references.length} references`}
              </span>
              <span className="truncate text-[14px] font-semibold text-co-text">{first.title}</span>
            </span>
          </div>
        ) : null}
        <label className="flex flex-col gap-1.5">
          <span className="ref-mono-label">Name</span>
          <input
            autoFocus
            value={name}
            maxLength={NAME_MAX}
            onChange={(e) => setName(e.target.value)}
            className="ref-field co-glass-control"
            aria-label="Collection name"
          />
        </label>
        <ReferenceFieldRow label="For">
          <ReferenceBrandSelect
            label="For"
            brands={brandChoices}
            value={brandId}
            allowEveryone={allowEveryone}
            onChange={setBrandId}
          />
        </ReferenceFieldRow>
        <button type="submit" disabled={!trimmed} className="ref-primary-lg">
          Create
        </button>
      </div>
    </form>
  );
}
